/**
 * Gmail-style inbox search: words, "exact phrases", -excluded words and
 * operators such as from:, subject:, is:unread or after:2026/10/01.
 *
 * Parsing only - no database - so the server runs a search from it and the
 * inbox can say which operators it did not understand.
 */

import type { Category } from "@/components/admin/inbox/mock-data";

export type SearchFolder = "inbox" | "sent" | "drafts" | "junk" | "trash";

export type ParsedSearch = {
  /** Words and phrases that must each appear (sender, subject or text). */
  words: string[];
  /** Words and phrases that must not appear. */
  exclude: string[];
  /** Sender name or address of any message (from:). */
  from: string[];
  /** Recipient address of any message, or the customer we wrote to (to:). */
  to: string[];
  subject: string[];
  category?: Category;
  /** Undefined: everything but junk and trash, as in Gmail. */
  folder?: SearchFolder | "anywhere";
  status?: "open" | "closed";
  unread?: boolean;
  starred?: boolean;
  assigned?: "me" | "anyone" | "nobody";
  hasNote?: boolean;
  hasBooking?: boolean;
  booking?: number;
  /** ISO timestamps on the thread's latest message. */
  after?: string;
  before?: string;
  /** Operators that were recognised but cannot be searched, or bad values. */
  ignored: string[];
};

/** What the search box help lists, in order. */
export const SEARCH_OPERATORS: [example: string, meaning: string][] = [
  ["sundus booking", "Threads with both words"],
  ['"charged for 8"', "Exact phrase"],
  ["-newsletter", "Leave out threads with a word"],
  ["from:sundus", "Sent by (name or address)"],
  ["to:ap@acme.com", "Sent to"],
  ["subject:invoice", "Word in the subject"],
  ["#1234  or  booking:1234", "Linked to a booking"],
  [
    "category:invoice",
    "enquiry, change, cancellation, invoice, road-show, other",
  ],
  ["is:unread  is:read", "Your read state"],
  ["is:starred", "Your favorites"],
  ["is:open  is:done", "Open or done"],
  ["is:mine  is:assigned  is:unassigned", "Assignment"],
  ["has:note  has:booking", "Has an internal note / a linked booking"],
  [
    "in:sent  in:junk  in:trash  in:anywhere",
    "Folder (junk and trash are left out unless asked)",
  ],
  ["after:2026/10/01  before:2026/10/09", "Latest message date"],
  ["newer_than:7d  older_than:1m", "Days (d), months (m) or years (y)"],
];

const CATEGORY_NAMES: Record<string, Category> = {
  enquiry: "enquiry",
  enquiries: "enquiry",
  change: "change",
  "change-request": "change",
  cancellation: "cancellation",
  cancel: "cancellation",
  invoice: "invoice",
  "road-show": "road-show",
  roadshow: "road-show",
  "on-the-road": "road-show",
  other: "other",
};

const FOLDERS: Record<string, ParsedSearch["folder"]> = {
  inbox: "inbox",
  sent: "sent",
  drafts: "drafts",
  draft: "drafts",
  junk: "junk",
  spam: "junk",
  trash: "trash",
  anywhere: "anywhere",
  all: "anywhere",
};

/** 2026/10/01, 2026-10-01 or 2026/1/5, as the start of that day (UTC). */
function parseDate(value: string): string | undefined {
  const match = value.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (!match) return undefined;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** 7d, 2m or 1y back from now. */
function parseAge(value: string, now: Date): string | undefined {
  const match = value.match(/^(\d{1,4})([dmy])$/i);
  if (!match) return undefined;
  const amount = Number(match[1]);
  const date = new Date(now);
  const unit = match[2].toLowerCase();
  if (unit === "d") date.setUTCDate(date.getUTCDate() - amount);
  if (unit === "m") date.setUTCMonth(date.getUTCMonth() - amount);
  if (unit === "y") date.setUTCFullYear(date.getUTCFullYear() - amount);
  return date.toISOString();
}

const unquote = (value: string) => value.replace(/^"|"$/g, "").trim();

/**
 * Splits a search into words, phrases and operators. An operator Gmail has
 * but this inbox does not (has:attachment) is listed in `ignored`; an
 * unknown "word:with:colons" is searched as a word.
 */
export function parseSearch(text: string, now = new Date()): ParsedSearch {
  const parsed: ParsedSearch = {
    words: [],
    exclude: [],
    from: [],
    to: [],
    subject: [],
    ignored: [],
  };

  const token = /(-?)([a-z_]+):("[^"]*"?|\S+)|(-?)"([^"]*)"?|(-?)(\S+)/gi;
  for (const match of text.matchAll(token)) {
    const [raw] = match;
    if (match[2] !== undefined) {
      const negated = match[1] === "-";
      const key = match[2].toLowerCase();
      const value = unquote(match[3]);
      const lower = value.toLowerCase();
      if (!value) continue;

      const flag = (on: boolean) => (negated ? !on : on);
      switch (key) {
        case "from":
          if (negated) parsed.ignored.push(raw);
          else parsed.from.push(value);
          continue;
        case "to":
          if (negated) parsed.ignored.push(raw);
          else parsed.to.push(value);
          continue;
        case "subject":
          if (negated) parsed.ignored.push(raw);
          else parsed.subject.push(value);
          continue;
        case "category":
        case "label":
          if (CATEGORY_NAMES[lower]) parsed.category = CATEGORY_NAMES[lower];
          else parsed.ignored.push(raw);
          continue;
        case "in":
          if (lower === "done") parsed.status = negated ? "open" : "closed";
          else if (lower === "open")
            parsed.status = negated ? "closed" : "open";
          else if (FOLDERS[lower]) parsed.folder = FOLDERS[lower];
          else parsed.ignored.push(raw);
          continue;
        case "is":
          if (lower === "unread") parsed.unread = flag(true);
          else if (lower === "read") parsed.unread = flag(false);
          else if (lower === "starred" || lower === "favorite")
            parsed.starred = flag(true);
          else if (lower === "done" || lower === "closed")
            parsed.status = negated ? "open" : "closed";
          else if (lower === "open")
            parsed.status = negated ? "closed" : "open";
          else if (lower === "mine") parsed.assigned = "me";
          else if (lower === "assigned")
            parsed.assigned = negated ? "nobody" : "anyone";
          else if (lower === "unassigned")
            parsed.assigned = negated ? "anyone" : "nobody";
          else parsed.ignored.push(raw);
          continue;
        case "has":
          if (lower === "note" || lower === "notes")
            parsed.hasNote = flag(true);
          else if (lower === "booking") parsed.hasBooking = flag(true);
          else parsed.ignored.push(raw);
          continue;
        case "booking": {
          const id = Number(value.replace(/^#/, ""));
          if (Number.isInteger(id) && id > 0) parsed.booking = id;
          else parsed.ignored.push(raw);
          continue;
        }
        case "after":
        case "since": {
          const date = parseDate(value);
          if (date) parsed.after = date;
          else parsed.ignored.push(raw);
          continue;
        }
        case "before": {
          const date = parseDate(value);
          if (date) parsed.before = date;
          else parsed.ignored.push(raw);
          continue;
        }
        case "newer_than":
        case "older_than": {
          const date = parseAge(value, now);
          if (!date) parsed.ignored.push(raw);
          else if (key === "newer_than") parsed.after = date;
          else parsed.before = date;
          continue;
        }
        default:
          // Not an operator: "re:" or "10:30" is just text.
          (negated ? parsed.exclude : parsed.words).push(
            `${match[2]}:${value}`,
          );
          continue;
      }
    }

    const negated = (match[4] ?? match[6]) === "-";
    const word = (match[5] ?? match[7] ?? "").trim();
    if (!word) continue;
    const booking = word.match(/^#(\d{1,10})$/);
    if (booking && !negated) {
      parsed.booking = Number(booking[1]);
      continue;
    }
    (negated ? parsed.exclude : parsed.words).push(word);
  }
  return parsed;
}
